import { TrendingDown, TrendingUp } from 'lucide-react';
import type { ReactNode } from 'react';

import { InfoIcon } from '@/app/components/ui/Card';
import { cn } from '@/lib/cn';
import { formatNumber } from '@/lib/format';

/** A period-over-period change to show under a `StatTile`'s value.
 * `goodDirection` says which way is the improvement, so a rising value can
 * be shown as a warning (e.g. stress) just as easily as a good one (e.g.
 * vitality) — the tile itself doesn't know what "better" means for its
 * metric. */
export interface StatTileDelta {
  pct: number;
  goodDirection: 'up' | 'down';
}

/** A small metric tile — vervangt `.stat-tile`. */
export function StatTile({
  label,
  value,
  delta,
  indicator,
  info,
  className,
}: {
  label: ReactNode;
  value: ReactNode;
  /** Omit when there's no real previous-period figure to compare against —
   * never show an invented or approximate delta. */
  delta?: StatTileDelta | null;
  /** A small marker before the label — e.g. the same provenance dot
   * `WhatYouSeeLegend` uses, so a tile visibly ties back to its row there. */
  indicator?: ReactNode;
  /** Tooltip text for a small info icon after the label. */
  info?: string;
  className?: string;
}) {
  return (
    <div
      className={cn('border-line flex min-w-32 flex-col gap-0.5 rounded-2xl border p-4', className)}
    >
      <span className="flex items-center justify-between gap-3">
        <span className="text-accent flex items-center gap-1.5 text-[13px]">
          {indicator}
          {label}
          {info && <InfoIcon text={info} />}
        </span>
        {delta != null && <StatTileDeltaBadge delta={delta} />}
      </span>
      <span className="font-heading text-ink text-[28px] font-semibold">{value}</span>
    </div>
  );
}

/** A delta's rising/falling badge (arrow + signed percent, colored by
 * whether that direction is the good one) — exported so other cards that
 * show the same period-over-period delta outside a `StatTile` (e.g. a
 * Trends chart card's header) can reuse the exact same look. */
export function StatTileDeltaBadge({ delta }: { delta: StatTileDelta }) {
  const rising = delta.pct > 0;
  const isGood =
    delta.pct === 0 || (rising ? delta.goodDirection === 'up' : delta.goodDirection === 'down');
  const Icon = rising ? TrendingUp : TrendingDown;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 text-xs font-medium',
        delta.pct === 0 ? 'text-accent' : isGood ? 'text-mint-deep' : 'text-danger',
      )}
    >
      {delta.pct !== 0 && <Icon className="size-3.5" aria-hidden="true" />}
      {formatNumber(delta.pct, { maximumFractionDigits: 0, signDisplay: 'exceptZero' })}%
    </span>
  );
}
